import type { CSSProperties } from "react"
import { useTranslation } from "react-i18next"
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
  getAvatarColor,
} from "@nexus/ui"
import { humanizePersonaTitle, personaInitials } from "../../lib/persona-format"
import type { Persona } from "../../types/personas.types"

const INTRO_AVATAR_MAX = 5
const AVATAR_FALLBACK_TEXT_COLOR = "hsl(0, 0%, 25%)"
/** Horizontal fan step (px) from stack center on group hover. */
const AVATAR_FAN_STEP_PX = 6

type AvatarFanStyle = CSSProperties & { "--avatar-fan-x": string }

export interface PersonasOnboardingIntroProps {
  onStart: () => void
  canStart?: boolean
  /** Active org label from the tenant switcher (e.g. "Taboola", "TPJ"). */
  organizationName?: string | null
  /** Suggested personas — drives the avatar stack (images when `avatarUrl` is set). */
  personas?: Persona[]
}

/** First-run intro — Figma Make onboarding shell (avatar stack + two stage tiles). */
export function PersonasOnboardingIntro({
  onStart,
  canStart = true,
  organizationName,
  personas = [],
}: PersonasOnboardingIntroProps) {
  const { t } = useTranslation()
  const avatars = personas.slice(0, INTRO_AVATAR_MAX)

  return (
    <section
      data-testid="personas-onboarding-intro"
      className="mx-auto flex w-full max-w-3xl flex-col items-center px-2 py-6"
    >
      <div
        className={cn(
          "flex w-full flex-col items-center gap-7 rounded-md border border-dashed border-border bg-card px-6 py-10 text-center sm:px-10 sm:py-[60px]",
        )}
      >
        {avatars.length > 0 ? (
          <TooltipProvider delayDuration={120} skipDelayDuration={0} disableHoverableContent>
            <div
              className="group/avatars flex -space-x-3.5"
              data-testid="personas-onboarding-intro-avatars"
            >
              {avatars.map((persona, index) => {
                const displayTitle = humanizePersonaTitle(persona.title)
                const initials = personaInitials(displayTitle)
                const fanFromCenter = index - (avatars.length - 1) / 2
                const fanStyle: AvatarFanStyle = {
                  zIndex: index + 1,
                  "--avatar-fan-x": `${fanFromCenter * AVATAR_FAN_STEP_PX}px`,
                }
                return (
                  <Tooltip key={persona.id}>
                    <TooltipTrigger asChild>
                      <span
                        className={cn(
                          "relative inline-flex rounded-full outline-none",
                          "transition-transform duration-150 ease-out",
                          "group-hover/avatars:translate-x-[var(--avatar-fan-x)]",
                          "hover:z-20 hover:scale-110 hover:-translate-y-1",
                          "focus-visible:ring-2 focus-visible:ring-ring/40",
                          "motion-reduce:transition-none",
                          "motion-reduce:group-hover/avatars:translate-x-0",
                          "motion-reduce:hover:scale-100 motion-reduce:hover:translate-y-0",
                        )}
                        style={fanStyle}
                      >
                        <Avatar className="size-11 border-2 border-background sm:size-12">
                          {persona.avatarUrl ? (
                            <AvatarImage src={persona.avatarUrl} alt={displayTitle} />
                          ) : null}
                          <AvatarFallback
                            style={{
                              backgroundColor: getAvatarColor(persona.personalityKey),
                              color: AVATAR_FALLBACK_TEXT_COLOR,
                            }}
                          >
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" sideOffset={10} className="max-w-[220px] text-center font-medium">
                      {displayTitle}
                    </TooltipContent>
                  </Tooltip>
                )
              })}
            </div>
          </TooltipProvider>
        ) : null}

        <div className="flex max-w-xl flex-col gap-2">
          <h2 className="text-[22px] font-semibold tracking-tight text-foreground">
            {t("personas.onboarding.intro.title", "Let's build your persona set")}
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {organizationName
              ? t("personas.onboarding.intro.descriptionWithOrganization", {
                  organization: organizationName,
                })
              : t("personas.onboarding.intro.description", "Adge analyzed your creative library — tagging every ad by the customer it speaks to. Confirm the personas that describe your own customers, then round them out with your market.")}
          </p>
        </div>

        <div className="grid w-full max-w-[660px] gap-3.5 sm:grid-cols-2">
          <StageCard
            indexLabel={t("personas.onboarding.intro.stage1Index", "01 — YOUR BRAND")}
            title={t("personas.onboarding.intro.stage1Title", "Review brand personas")}
            description={t("personas.onboarding.intro.stage1Description", "Swipe through the customer types in your own ads. Accept, reject or refine each.")}
            accent="brand"
          />
          <StageCard
            indexLabel={t("personas.onboarding.intro.stage2Index", "02 — YOUR MARKET")}
            title={t("personas.onboarding.intro.stage2Title", "Add industry personas")}
            description={t("personas.onboarding.intro.stage2Description", "Pull in high-presence personas your competitors run that you don't.")}
            accent="market"
          />
        </div>

        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button size="lg" iconStart="plus" onClick={onStart} disabled={!canStart}>
                  {t("personas.onboarding.intro.cta", "Analyze my brand")}
                </Button>
              </span>
            </TooltipTrigger>
            {!canStart && (
              <TooltipContent>
                <p>{t("common.noPermission", "You don't have permission to do this")}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>
    </section>
  )
}

function StageCard({
  indexLabel,
  title,
  description,
  accent,
}: {
  indexLabel: string
  title: string
  description: string
  accent: "brand" | "market"
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-border bg-muted px-4 py-4 text-left">
      <p
        className={cn(
          "font-mono text-[11px]",
          accent === "brand" ? "text-primary" : "text-[var(--competitor)]",
        )}
      >
        {indexLabel}
      </p>
      <p className="text-[13px] font-semibold text-foreground">{title}</p>
      <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
    </div>
  )
}
